import { useState, useEffect, CSSProperties } from 'react'
import { Paper, Stack, Text, Loader, Box } from '@mantine/core'
import { useNavigate } from '@tanstack/react-router'
import { List, useDynamicRowHeight } from 'react-window'
import { ResponsiveTitle } from '../ResponsiveTitle/ResponsiveTitle'
import { supabase } from '../../lib/supabase'
import styles from './CommentsSummary.module.css'

interface GuestComment {
  guest_id: string
  guest_name: string
  comment: string
}

export function CommentsSummary() {
  const navigate = useNavigate()
  const [comments, setComments] = useState<GuestComment[]>([])
  const [loading, setLoading] = useState(true)
  
  const dynamicRowHeight = useDynamicRowHeight({
    defaultRowHeight: 100,
  })

  const fetchComments = async () => {
    try {
      setLoading(true)

      const { data: guests, error } = await supabase
        .from('guests')
        .select('id, primary_name, comments')
        .not('comments', 'is', null)
        .neq('comments', '')

      if (error) throw error

      const guestComments: GuestComment[] = (guests || []).map(g => ({
        guest_id: g.id,
        guest_name: g.primary_name,
        comment: g.comments || '',
      }))

      setComments(guestComments)
    } catch (err) {
      console.error('Error fetching comments:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchComments()
  }, [])

  const Row = ({ index, style }: { index: number; style: CSSProperties }) => {
    const comment = comments[index]
    
    if (!comment) {
      return <div style={style} />
    }

    const handleNameClick = () => {
      navigate({ 
        to: `/admin/guest/${comment.guest_id}`,
        search: {
          page: 1,
          pageSize: 5,
          sortField: 'name',
          sortDirection: 'asc',
          nameFilter: ''
        }
      })
    }

    return (
      <div style={style}>
        <div className={styles.commentRow}>
          <Text 
            size="sm" 
            fw={600} 
            c="golden" 
            mb="xs"
            onClick={handleNameClick}
            className={styles.guestName}
          >
            {comment.guest_name}
          </Text>
          <Text size="sm" c="dimmed">
            {comment.comment}
          </Text>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <Paper p="xl" shadow="sm" className={styles.container}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Lade Kommentare...</Text>
        </Stack>
      </Paper>
    )
  }

  if (comments.length === 0) {
    return (
      <Paper p="xl" shadow="sm" className={styles.container}>
        <Stack gap="lg">
          <ResponsiveTitle order={2} className={styles.title}>
            Gast-Kommentare
          </ResponsiveTitle>
          <Text c="dimmed" ta="center">
            Keine Kommentare verfügbar
          </Text>
        </Stack>
      </Paper>
    )
  }

  return (
    <Paper p="xl" shadow="sm" className={styles.container}>
      <Stack gap="lg">
        <ResponsiveTitle order={2} className={styles.title}>
          Gast-Kommentare
        </ResponsiveTitle>

        <Text size="sm" fw={600} c="golden">
          Alle Kommentare ({comments.length})
        </Text>

        {/* Virtualized Comments List */}
        <Box className={styles.listContainer}>
          <List
            rowComponent={Row}
            rowCount={comments.length}
            rowHeight={dynamicRowHeight}
            rowProps={{} as any}
            style={{ height: 400 }}
          />
        </Box>
      </Stack>
    </Paper>
  )
}
